import { Alert, Badge, Button, Card, Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { format, isValid, type Locale } from 'date-fns';
import { ja, enUS, zhCN, fr, es, de, pt, ru } from 'date-fns/locale';
import api from '../../services/api';
import {
  buildAdminMailtoHref,
  COMMUNICATION_BOARD_UI_ENABLED,
  SYSTEM_ADMIN_EMAIL,
} from '../../utils/config';
import surface from './KensukeSurface.module.scss';

interface FeatureRequest {
  id: number;
  title: string;
  description: string;
  category: string;
  status?: string;
  adminResponse?: string | null;
  admin_response?: string | null;
  vote_count?: number;
  voteCount?: number;
  created_at: string;
  createdAt?: string;
}

const DATE_LOCALES: Record<string, Locale> = {
  ja,
  en: enUS,
  'zh-CN': zhCN,
  zh: zhCN,
  fr,
  es,
  de,
  pt,
  ru,
};

const CATEGORY_KEYS: Record<string, string> = {
  feature: 'feature_requests.category_feature',
  improvement: 'feature_requests.category_improvement',
  bug_fix: 'feature_requests.category_bug_fix',
  ui_ux: 'feature_requests.category_ui_ux',
  integration: 'feature_requests.category_integration',
  other: 'feature_requests.category_other',
};

const STATUS_KEYS: Record<string, string> = {
  under_review: 'feature_requests.status_under_review',
  planned: 'feature_requests.status_planned',
  in_progress: 'feature_requests.status_in_progress',
  completed: 'feature_requests.status_completed',
  rejected: 'feature_requests.status_rejected',
  duplicate: 'feature_requests.status_duplicate',
};

function statusVariant(status?: string | null): string {
  switch (status) {
    case 'planned':
      return 'info';
    case 'in_progress':
      return 'primary';
    case 'completed':
      return 'success';
    case 'rejected':
      return 'danger';
    case 'duplicate':
      return 'dark';
    default:
      return 'secondary';
  }
}

function truncate(text: string, max = 100): string {
  return text.length > max ? `${text.slice(0, max)}...` : text;
}

function RequestRow({
  request,
  footer,
  t,
}: {
  request: FeatureRequest;
  footer: string;
  t: (key: string, opts?: { defaultValue?: string }) => string;
}) {
  const hasResponse = Boolean(request.adminResponse || request.admin_response);
  return (
    <div className="mb-3 pb-3 border-bottom">
      <div className="d-flex flex-wrap gap-2 mb-2">
        <Badge bg="secondary">
          {t(CATEGORY_KEYS[request.category] || 'feature_requests.category_other', {
            defaultValue: 'その他',
          })}
        </Badge>
        <Badge bg={statusVariant(request.status)}>
          {t(STATUS_KEYS[request.status || ''] || 'feature_requests.status_under_review')}
        </Badge>
        {hasResponse ? (
          <Badge bg="success">{t('feature_requests.response_present')}</Badge>
        ) : (
          <Badge bg="light" text="dark">
            {t('feature_requests.response_pending')}
          </Badge>
        )}
      </div>
      <h6 className="mb-1">{request.title}</h6>
      <p className="small text-muted mb-1">{truncate(request.description)}</p>
      <small className="text-muted">{footer}</small>
    </div>
  );
}

function RequestColumn({
  title,
  isError,
  requests,
  emptyLabel,
  errorLabel,
  footerFor,
}: {
  title: string;
  isError: boolean;
  requests: FeatureRequest[] | undefined;
  emptyLabel: string;
  errorLabel: string;
  footerFor: (r: FeatureRequest) => string;
}) {
  const { t } = useTranslation();
  return (
    <Col md={6}>
      <Card className={`h-100 ${surface.requestsCard}`}>
        <Card.Body>
          <h5 className="card-title fw-semibold mb-3">{title}</h5>
          {isError ? (
            <Alert variant="warning" className="mb-0">
              <small>{errorLabel}</small>
            </Alert>
          ) : requests && requests.length > 0 ? (
            requests.map((request) => (
              <RequestRow key={request.id} request={request} footer={footerFor(request)} t={t} />
            ))
          ) : (
            <p className="text-muted small mb-0">{emptyLabel}</p>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
}

async function fetchFeatureRequests(sort: 'popular' | 'recent'): Promise<FeatureRequest[]> {
  try {
    const response = await api.get(`/feature-requests?sort=${sort}&limit=5`);
    return response.data.data || [];
  } catch (error) {
    console.error(`Error loading ${sort} feature requests:`, error);
    return [];
  }
}

export default function DashboardFeatureRequests() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { data: popularRequests, isError: isPopularError } = useQuery({
    queryKey: ['feature-requests', 'popular'],
    queryFn: () => fetchFeatureRequests('popular'),
    retry: false,
    staleTime: 1000 * 60 * 5,
    enabled: COMMUNICATION_BOARD_UI_ENABLED,
  });

  const { data: recentRequests, isError: isRecentError } = useQuery({
    queryKey: ['feature-requests', 'recent'],
    queryFn: () => fetchFeatureRequests('recent'),
    retry: false,
    staleTime: 1000 * 60 * 5,
    enabled: COMMUNICATION_BOARD_UI_ENABLED,
  });

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (!isValid(date)) return '-';
    const lang = i18n.resolvedLanguage || i18n.language || 'ja';
    const locale = DATE_LOCALES[lang] || DATE_LOCALES[lang.split('-')[0]] || enUS;
    return format(date, lang === 'ja' ? 'yyyy/MM/dd HH:mm' : 'yyyy-MM-dd HH:mm', { locale });
  };

  if (!COMMUNICATION_BOARD_UI_ENABLED) {
    return (
      <section id="contact-admin" className={surface.requestsSection}>
        <Container>
          <h2 className={surface.requestsHeading}>
            {t('home.admin_contact_title', { defaultValue: 'お問い合わせ' })}
          </h2>
          <p className="text-muted mb-3">
            {t('home.admin_contact_subtitle', {
              defaultValue:
                'ご質問、不具合の報告、機能要望などは、システム管理者までメールでお送りください。',
            })}
          </p>
          <Button variant="primary" size="sm" as="a" href={buildAdminMailtoHref()}>
            {t('home.nav_contact', { defaultValue: 'お問い合わせ' })}
          </Button>
          <p className="text-muted mt-2 mb-0 small">
            <a href={buildAdminMailtoHref()}>{SYSTEM_ADMIN_EMAIL}</a>
          </p>
        </Container>
      </section>
    );
  }

  const empty = t('home.feature_requests_no_requests');
  const loadError = t('home.feature_requests_load_error');

  return (
    <section id="feature-requests" className={surface.requestsSection}>
      <Container>
        <div className="mb-4">
          <h2 className={surface.requestsHeading}>{t('home.feature_requests_title')}</h2>
          <p className="text-muted mb-1">{t('home.feature_requests_subtitle')}</p>
          <p className="text-muted small mb-0">{t('home.feature_requests_login_note')}</p>
        </div>
        <Row className="g-3 mb-3">
          <RequestColumn
            title={t('home.feature_requests_popular')}
            isError={isPopularError}
            requests={popularRequests}
            emptyLabel={empty}
            errorLabel={loadError}
            footerFor={(r) => String(r.vote_count ?? r.voteCount ?? 0)}
          />
          <RequestColumn
            title={t('home.feature_requests_recent')}
            isError={isRecentError}
            requests={recentRequests}
            emptyLabel={empty}
            errorLabel={loadError}
            footerFor={(r) => formatDate(r.created_at || r.createdAt)}
          />
        </Row>
        <div>
          <Button variant="outline-primary" size="sm" onClick={() => navigate('/contact')}>
            {t('home.feature_requests_view')}
          </Button>
        </div>
      </Container>
    </section>
  );
}
